import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartPlus, faCartShopping, faCheck, faCircleInfo, faHeart, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { getFecharPedidoUrl, openTelegramVendas } from '../openVendas'
import instance from '../apiInstance'
import config from '../config'
import { appCopy } from '../pages/appCopy'
import {
  addStoryCartItem,
  bumpStoryCartItem,
  CART_CHANGED,
  fmtBrl,
  hydrateStoryCartPrices,
  prettyCartLabel,
  readStoryCart,
  storyCartCount,
  storyCartTotal,
  storyCartUnpriced,
  writeStoryCart,
} from '../storyCart'
import { resolveCartPhotoPrices } from '../ocrPhotoPrice'
import './StoryCartDock.css'
import '../telegramWebApp'

function previewCart() {
  try {
    return new URLSearchParams(window.location.search).get('cart') === '1'
  } catch {
    return false
  }
}

function closeUrlFromQuery() {
  try {
    const raw = new URLSearchParams(window.location.search).get('tgClose') || ''
    if (!raw.startsWith('http://') && !raw.startsWith('https://')) return ''
    if (!raw.includes('/api/tg/pwa-cart/close')) return ''
    return raw
  } catch {
    return ''
  }
}

function closeViaParent(payload) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || window.parent === window) {
      resolve(null)
      return
    }
    const id = `c${Date.now()}`
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMsg)
      resolve(null)
    }, 12000)
    function onMsg(event) {
      if (event.data?.type !== 'tg-pwa-cart-close-result' || event.data.id !== id) return
      window.clearTimeout(timer)
      window.removeEventListener('message', onMsg)
      resolve(event.data.result || null)
    }
    window.addEventListener('message', onMsg)
    window.parent.postMessage({ type: 'tg-pwa-cart-close', id, payload }, '*')
  })
}

async function postCartClose(payload) {
  const fromParent = await closeViaParent(payload)
  if (fromParent && typeof fromParent === 'object') return fromParent

  const host = window.location.hostname
  const urls = []
  if (host === 'localhost' || host === '127.0.0.1') {
    urls.push('http://127.0.0.1:3001/api/tg/pwa-cart/close')
    urls.push('http://localhost:3001/api/tg/pwa-cart/close')
  }
  const apiBase = String(config.API_URL || '').replace(/\/$/, '')
  if (apiBase) urls.push(`${apiBase}/tg/pwa-cart/close`)
  urls.push('https://api-web-stories.vercel.app/tg/pwa-cart/close')
  const fromQuery = closeUrlFromQuery()
  if (fromQuery) urls.push(fromQuery)
  if (config.TG_CART_CLOSE_URL) urls.push(config.TG_CART_CLOSE_URL)

  let last = { ok: false, reason: 'unreachable' }
  const seen = new Set()
  for (const url of urls) {
    if (!url || seen.has(url)) continue
    seen.add(url)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => null)
      if (data && typeof data === 'object') {
        last = data
        if (data.ok) return data
      }
    } catch {
      /* tenta o próximo host */
    }
  }
  return last
}

function useCartAllowed() {
  const { token } = useSelector((state) => state.auth)
  const [allowed, setAllowed] = useState(false)
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (previewCart() || token) setAllowed(true)
    if (!token) return undefined
    let cancelled = false
    instance.get('/me')
      .then((res) => {
        const nextEmail = String(res.data?.user?.email || '').trim().toLowerCase()
        if (cancelled) return
        setEmail(nextEmail)
      })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [token])

  return { allowed, email }
}

function useCartItems() {
  const [items, setItems] = useState([])
  const refresh = useCallback(() => setItems(readStoryCart()), [])

  useEffect(() => {
    refresh()
    window.addEventListener(CART_CHANGED, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(CART_CHANGED, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [refresh])

  return items
}

function useHydrateCartPrices(items) {
  const [pricing, setPricing] = useState(false)
  const failed = useRef(new Set())
  const inflight = useRef(new Set())

  useEffect(() => {
    const need = (items || []).filter((item) => (
      item.price == null
      && item.url
      && !failed.current.has(item.url)
      && !inflight.current.has(item.url)
    ))
    if (!need.length) return undefined
    const urls = need.map((item) => item.url)
    urls.forEach((url) => inflight.current.add(url))
    let cancelled = false
    setPricing(true)
    resolveCartPhotoPrices(urls)
      .then((prices) => {
        if (cancelled) return
        hydrateStoryCartPrices(prices)
        urls.forEach((url) => {
          const key = String(url).split('?')[0]
          if (prices[url] == null && prices[key] == null) failed.current.add(url)
        })
      })
      .catch(() => {
        urls.forEach((url) => failed.current.add(url))
      })
      .finally(() => {
        urls.forEach((url) => inflight.current.delete(url))
        if (!cancelled) setPricing(false)
      })
    return () => { cancelled = true }
  }, [items])

  return pricing
}

export function AddToCartButton({ currentItem, disabled, variant = 'pill' }) {
  const t = appCopy(config.STORE_LOCALE)
  const { allowed } = useCartAllowed()
  const items = useCartItems()
  const inCart = Boolean(
    currentItem?.id && items.some((item) => item.id === String(currentItem.id)),
  )

  if (!allowed || !currentItem?.id) return null

  const onAdd = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    addStoryCartItem(currentItem)
  }

  const title = inCart
    ? t.added
    : currentItem.price != null ? t.addPrice(fmtBrl(currentItem.price)) : t.addPiece

  if (variant === 'icon') {
    return (
      <button
        type="button"
        className={`story-add-icon${inCart ? ' is-on' : ''}`}
        disabled={disabled}
        onClick={onAdd}
        title={title}
        aria-label={title}
      >
        <FontAwesomeIcon icon={inCart ? faCheck : faCartPlus} />
      </button>
    )
  }

  return (
    <button
      type="button"
      className={`story-add-btn${inCart ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={onAdd}
      title={title}
    >
      <FontAwesomeIcon icon={inCart ? faCheck : faCartPlus} />
      <span>{inCart ? t.added : t.add}</span>
    </button>
  )
}

function usePinnedDock(active) {
  useEffect(() => {
    if (!active) return undefined
    const root = document.documentElement
    const pin = () => {
      const vv = window.visualViewport
      if (!vv) {
        root.style.setProperty('--vv-top', '0px')
        root.style.setProperty('--vv-height', `${window.innerHeight}px`)
        return
      }
      root.style.setProperty('--vv-top', `${Math.round(vv.offsetTop)}px`)
      root.style.setProperty('--vv-height', `${Math.round(vv.height)}px`)
    }
    pin()
    const opts = { passive: true }
    window.visualViewport?.addEventListener('resize', pin, opts)
    window.visualViewport?.addEventListener('scroll', pin, opts)
    window.addEventListener('resize', pin, opts)
    window.addEventListener('orientationchange', pin)
    return () => {
      window.visualViewport?.removeEventListener('resize', pin)
      window.visualViewport?.removeEventListener('scroll', pin)
      window.removeEventListener('resize', pin)
      window.removeEventListener('orientationchange', pin)
      root.style.removeProperty('--vv-top')
      root.style.removeProperty('--vv-height')
    }
  }, [active])
}

const FOOTER_DOCK_PATHS = ['/home', '/dashboard', '/auctions', '/points']

export function TesteeAppDock() {
  const { pathname } = useLocation()
  if (!FOOTER_DOCK_PATHS.includes(pathname)) return null
  return <StoryCartDock variant="footer" />
}

export default function StoryCartDock({ currentItem, variant = 'fab' }) {
  const t = appCopy(config.STORE_LOCALE)
  const openAtendente = useCallback((e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    void openTelegramVendas(null, currentItem || undefined)
  }, [currentItem])
  const { allowed, email } = useCartAllowed()
  const items = useCartItems()
  const pricing = useHydrateCartPrices(items)
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState(null)
  const [favs, setFavs] = useState([])
  const [favsLoading, setFavsLoading] = useState(false)
  const [closing, setClosing] = useState(false)
  const [closeMsg, setCloseMsg] = useState('')
  const [zoomUrl, setZoomUrl] = useState('')
  const isFooter = variant === 'footer'
  const cartOpen = isFooter ? panel === 'cart' : open

  useEffect(() => {
    if (!isFooter || !allowed) return undefined
    document.body.classList.add('has-testee-dock')
    return () => document.body.classList.remove('has-testee-dock')
  }, [isFooter, allowed])

  useEffect(() => {
    if (panel !== 'fav') return undefined
    let cancelled = false
    setFavsLoading(true)
    instance.get('/favorites/')
      .then((res) => {
        if (!cancelled) setFavs(Array.isArray(res.data) ? res.data : [])
      })
      .catch(() => {
        if (!cancelled) setFavs([])
      })
      .finally(() => {
        if (!cancelled) setFavsLoading(false)
      })
    return () => { cancelled = true }
  }, [panel])

  usePinnedDock(isFooter && allowed)

  if (!allowed) return null

  const count = storyCartCount(items)
  const total = storyCartTotal(items)
  const unpriced = storyCartUnpriced(items)
  const alreadyIn = currentItem?.id && items.some((item) => item.id === String(currentItem.id))

  const closeOrder = async () => {
    if (!items.length || closing) return
    if (!email) {
      setCloseMsg(t.needLogin)
      return
    }
    setClosing(true)
    setCloseMsg('')
    const lines = items.map((item) => {
      const money = item.price != null ? fmtBrl(item.price * (item.qty || 1)) : t.toConfirm
      return `${item.qty}× ${prettyCartLabel(item)} — ${money}`
    })
    const totalLabel = total > 0
      ? (unpriced ? `${fmtBrl(total)} + ${unpriced} com preço na foto` : fmtBrl(total))
      : (unpriced ? t.toConfirm : fmtBrl(0))
    try {
      const data = await postCartClose({
        email,
        tg: String(window.Telegram?.WebApp?.initDataUnsafe?.user?.id || ''),
        items: items.map((item) => ({
          id: item.id,
          title: prettyCartLabel(item),
          brand: item.brand,
          url: item.url,
          qty: item.qty,
          price: item.price,
        })),
      })
      if (!data.ok) {
        setCloseMsg(t.closeFail)
        return
      }
      writeStoryCart([])
      openTelegramVendas(getFecharPedidoUrl({
        orderId: data.orderId,
        totalLabel,
        items: lines,
      }))
      setCloseMsg(data.saved ? t.cartSaved : t.cartNotSaved)
    } catch {
      setCloseMsg(t.botUnreach)
    } finally {
      setClosing(false)
    }
  }

  const toggleFooter = (next) => {
    setCloseMsg('')
    setPanel((prev) => (prev === next ? null : next))
  }

  const closePanel = () => (isFooter ? setPanel(null) : setOpen(false))

  const tree = (
    <div className={`story-cart${isFooter ? ' story-cart--footer' : ''}`} onClick={(e) => e.stopPropagation()}>
      {cartOpen ? (
        <div className={`story-cart-pop${isFooter ? ' story-cart-pop--dock' : ''}`} role="dialog" aria-label={t.cart}>
          <div className="story-cart-pop-head">
            <strong>{t.cart} {count ? `· ${count}` : ''}</strong>
            <button type="button" onClick={closePanel}>{t.close}</button>
          </div>
          {items.length === 0 ? (
            <p className="story-cart-empty">{t.cartEmpty}</p>
          ) : (
            <ul className="story-cart-list">
              {items.map((item) => (
                <li key={item.id} className="story-cart-line">
                  {item.url ? (
                    <button
                      type="button"
                      className="story-cart-thumb"
                      onClick={() => setZoomUrl(item.url)}
                      aria-label={t.seePhoto}
                    >
                      <img src={item.url} alt="" />
                    </button>
                  ) : null}
                  <span>
                    {prettyCartLabel(item)}
                    <small>{fmtBrl(item.price)}</small>
                  </span>
                  <button type="button" className="story-cart-qty" onClick={() => bumpStoryCartItem(item.id, -1)}>−</button>
                  <b>{item.qty}</b>
                  <button type="button" className="story-cart-qty" onClick={() => bumpStoryCartItem(item.id, 1)}>+</button>
                </li>
              ))}
            </ul>
          )}
          {currentItem?.id ? (
            <button
              type="button"
              className="story-cart-add"
              onClick={() => addStoryCartItem(currentItem)}
            >
              {alreadyIn ? t.addMore : t.addPiece}
              {currentItem.price != null ? ` · ${fmtBrl(currentItem.price)}` : ''}
            </button>
          ) : null}
          <div className="story-cart-total">
            <span>Total</span>
            <strong>
              {total > 0 ? fmtBrl(total) : unpriced ? t.toConfirm : fmtBrl(0)}
            </strong>
            {pricing ? (
              <small>{t.readingPrice}</small>
            ) : unpriced ? (
              <small>
                {total > 0 ? `${unpriced} ainda com preço na foto` : `${unpriced} com preço na foto`}
              </small>
            ) : null}
          </div>
          {closeMsg ? <p className="story-cart-empty">{closeMsg}</p> : null}
          <button
            type="button"
            className="story-cart-go"
            disabled={!count || closing}
            onClick={() => void closeOrder()}
          >
            {closing ? t.sendingBot : t.closeOrder}
          </button>
        </div>
      ) : null}
      {panel === 'fav' ? (
        <div className="story-cart-pop story-cart-pop--dock" role="dialog" aria-label={t.favorites}>
          <div className="story-cart-pop-head">
            <strong>{t.favorites}</strong>
            <button type="button" onClick={() => setPanel(null)}>{t.close}</button>
          </div>
          {favsLoading ? (
            <p className="story-cart-empty">{t.loadingDots}</p>
          ) : favs.length === 0 ? (
            <p className="story-cart-empty">{t.favEmpty}</p>
          ) : (
            <ul className="story-cart-list">
              {favs.map((fav) => {
                const url = fav.snapshot_image_url || ''
                const label = prettyCartLabel({
                  title: fav.snapshot_title,
                  brand: fav.snapshot_brand,
                  url,
                })
                return (
                  <li key={fav.id || url} className="story-cart-line">
                    {url ? (
                      <button
                        type="button"
                        className="story-cart-thumb"
                        onClick={() => setZoomUrl(url)}
                        aria-label={t.seePhoto}
                      >
                        <img src={url} alt="" />
                      </button>
                    ) : null}
                    <span>
                      {label}
                      <small>{fmtBrl(fav.snapshot_original_price)}</small>
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : null}
      {panel === 'info' ? (
        <div className="story-cart-pop story-cart-pop--dock" role="dialog" aria-label={t.infos}>
          <div className="story-cart-pop-head">
            <strong>{t.howTo}</strong>
            <button type="button" onClick={() => setPanel(null)}>{t.close}</button>
          </div>
          <p className="story-cart-info">
            <b>1.</b> {t.info1}
          </p>
          <p className="story-cart-info">
            <b>2.</b> {t.info2}
          </p>
          <p className="story-cart-info">
            <b>3.</b> {t.info3}
          </p>
          <p className="story-cart-info">
            <b>4.</b> {t.info4}
          </p>
          <a
            className="story-cart-go story-cart-chat"
            href="#vendas"
            onClick={openAtendente}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {t.talkSales}
          </a>
        </div>
      ) : null}
      {isFooter ? (
        <nav className="testee-dock" aria-label={t.menu}>
          <button
            type="button"
            className={`testee-dock-item${panel === 'cart' ? ' is-on' : ''}`}
            onClick={() => toggleFooter('cart')}
          >
            <span className="testee-dock-circle">
              <FontAwesomeIcon icon={faCartShopping} />
              {count > 0 ? (
                <span className="story-cart-badge">{count > 99 ? '99+' : count}</span>
              ) : null}
            </span>
            <span>{t.cart}</span>
          </button>
          <button
            type="button"
            className={`testee-dock-item${panel === 'fav' ? ' is-on' : ''}`}
            onClick={() => toggleFooter('fav')}
          >
            <span className="testee-dock-circle">
              <FontAwesomeIcon icon={faHeart} />
            </span>
            <span>{t.favorites}</span>
          </button>
          <a
            className="testee-dock-item"
            href="#vendas"
            aria-label={t.talkSales}
            onClick={openAtendente}
          >
            <span className="testee-dock-circle">
              <FontAwesomeIcon icon={faPaperPlane} />
            </span>
            <span>{t.salesChannel}</span>
          </a>
          <button
            type="button"
            className={`testee-dock-item${panel === 'info' ? ' is-on' : ''}`}
            onClick={() => toggleFooter('info')}
          >
            <span className="testee-dock-circle">
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
            <span>{t.infos}</span>
          </button>
        </nav>
      ) : (
        <button
          type="button"
          className="story-cart-fab"
          aria-label={t.cartAria(count)}
          onClick={() => setOpen((v) => !v)}
        >
          <FontAwesomeIcon icon={faCartShopping} />
          <span className="story-cart-badge">{count > 99 ? '99+' : count}</span>
        </button>
      )}
      {zoomUrl ? (
        <button
          type="button"
          className="story-cart-zoom"
          onClick={() => setZoomUrl('')}
          aria-label={t.closePhoto}
        >
          <img src={zoomUrl} alt={t.pieceLarger} />
          <span>{t.tapClose}</span>
        </button>
      ) : null}
    </div>
  )

  if (isFooter && typeof document !== 'undefined') {
    return createPortal(<div className="testee-dock-layer">{tree}</div>, document.body)
  }
  return tree
}
